import { resolveTokens } from '@/components/labelDesigner/LabelObjectRenderer';
import type { BarcodeSymbology, LabelObject, LabelTemplate } from '@/types/labelTemplate';

/**
 * Turns a label template into TSPL — the command language TSC printers speak — so a label can go
 * straight to the printer through the Hardware Bridge with no browser print dialog and no driver
 * rasterisation.
 *
 * Why generate commands instead of sending a picture of the label: barcodes and QR codes come out
 * of the printer's own encoder at native head resolution, so they scan reliably at small sizes.
 * A browser-rendered bitmap resampled to 203dpi is exactly how you get barcodes that "look fine
 * but won't scan".
 *
 * Known limit: logo/image objects are skipped. Sending images needs the raster BITMAP command with
 * the artwork converted to 1-bit — worth doing, but text/barcode/QR/box/line cover the labels this
 * module prints today, and silently emitting a wrong-looking label would be worse than omitting the
 * artwork and saying so (see skippedImages in the return value).
 */

/** TE210 and most TSC desktop units are 203dpi — 8 dots per mm. 300dpi models are 11.81. */
const DEFAULT_DOTS_PER_MM = 8;

export interface TsplBuildResult {
  commands: string;
  skippedImages: number;
}

export interface TsplOptions {
  dotsPerMm?: number;
  copies?: number;
  /** Label gap in mm — the vertical space between labels on the roll. */
  gapMm?: number;
}

const SYMBOLOGY_MAP: Record<BarcodeSymbology, string> = {
  CODE128: '128',
  CODE39: '39',
  EAN13: 'EAN13',
  UPC: 'UPCA',
  // TSPL's PDF417 is a separate command with a different signature; falling back to Code 128 keeps
  // the label scannable rather than emitting a command the printer will reject outright.
  PDF417: '128',
};

/** TSPL's internal bitmap fonts, by nominal height in dots. Picking the nearest one and scaling
 * with the multiplier gets close to the designer's pt size without needing downloaded fonts. */
const INTERNAL_FONTS: { id: string; heightDots: number }[] = [
  { id: '1', heightDots: 12 },
  { id: '2', heightDots: 16 },
  { id: '3', heightDots: 24 },
  { id: '4', heightDots: 32 },
  { id: '5', heightDots: 48 },
];

function escapeTspl(value: string): string {
  // Quotes terminate a TSPL string argument; backslash is its escape character.
  return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

/** pt → dots at the head's resolution (1pt = 1/72 inch). */
function pointsToDots(points: number, dotsPerMm: number): number {
  return Math.round((points / 72) * 25.4 * dotsPerMm);
}

function pickFont(fontSizePt: number, dotsPerMm: number): { font: string; multiplier: number } {
  const targetDots = pointsToDots(fontSizePt, dotsPerMm);
  let best = { font: '1', multiplier: 1, error: Number.MAX_VALUE };
  for (const font of INTERNAL_FONTS) {
    for (let multiplier = 1; multiplier <= 10; multiplier += 1) {
      const error = Math.abs(font.heightDots * multiplier - targetDots);
      if (error < best.error) best = { font: font.id, multiplier, error };
    }
  }
  return { font: best.font, multiplier: best.multiplier };
}

export function buildTsplLabel(template: LabelTemplate, sampleData: Record<string, string>, options: TsplOptions = {}): TsplBuildResult {
  const dpmm = options.dotsPerMm ?? DEFAULT_DOTS_PER_MM;
  const copies = options.copies ?? 1;
  const mm = (value: number) => Math.round(value * dpmm);

  const lines: string[] = [
    `SIZE ${template.widthMm} mm, ${template.heightMm} mm`,
    `GAP ${options.gapMm ?? 2} mm, 0`,
    'DIRECTION 1',
    'CLS',
  ];

  let skippedImages = 0;

  for (const object of template.objects as LabelObject[]) {
    const x = mm(object.x);
    const y = mm(object.y);

    switch (object.type) {
      case 'text': {
        const content = resolveTokens(object.text ?? '', sampleData);
        if (!content.trim()) break;
        const { font, multiplier } = pickFont(object.fontSize ?? 10, dpmm);
        // TSPL has no wrapping or alignment box: text is drawn from its origin. Right/centre
        // alignment is approximated by shifting the origin using the object's own width, which is
        // what the designer laid the text out against.
        const approxCharWidthDots = (INTERNAL_FONTS.find((f) => f.id === font)?.heightDots ?? 12) * multiplier * 0.6;
        const textWidthDots = content.length * approxCharWidthDots;
        const boxWidthDots = mm(object.w);
        const offset = object.align === 'center'
          ? Math.max(0, (boxWidthDots - textWidthDots) / 2)
          : object.align === 'right'
            ? Math.max(0, boxWidthDots - textWidthDots)
            : 0;
        lines.push(`TEXT ${x + Math.round(offset)},${y},"${font}",0,${multiplier},${multiplier},"${escapeTspl(content)}"`);
        break;
      }

      case 'barcode': {
        const data = resolveTokens(object.data ?? '', sampleData);
        if (!data.trim()) break;
        const heightDots = Math.max(8, mm(object.h) - (object.humanReadable === false ? 0 : 20));
        const readable = object.humanReadable === false ? 0 : 1;
        lines.push(`BARCODE ${x},${y},"${SYMBOLOGY_MAP[object.symbology] ?? '128'}",${heightDots},${readable},0,2,4,"${escapeTspl(data)}"`);
        break;
      }

      case 'qr': {
        const data = resolveTokens(object.data ?? '', sampleData);
        if (!data.trim()) break;
        // Cell width is derived from the drawn size so the printed symbol matches the layout; QR is
        // square, so the smaller dimension governs.
        const cell = Math.max(1, Math.min(10, Math.floor(Math.min(mm(object.w), mm(object.h)) / 25)));
        lines.push(`QRCODE ${x},${y},${object.errorCorrection ?? 'M'},${cell},A,0,"${escapeTspl(data)}"`);
        break;
      }

      case 'rectangle': {
        const thickness = Math.max(1, mm(object.borderWidth ?? 0.3));
        lines.push(`BOX ${x},${y},${x + mm(object.w)},${y + mm(object.h)},${thickness}`);
        break;
      }

      case 'line': {
        // A line is just a filled bar; a zero-height object still needs a visible stroke.
        const thickness = Math.max(1, mm(object.borderWidth ?? 0.3));
        const width = Math.max(1, mm(object.w));
        lines.push(`BAR ${x},${y},${width},${thickness}`);
        break;
      }

      case 'table': {
        const rowHeightDots = mm(object.rowHeight ?? 5);
        const { font, multiplier } = pickFont(object.fontSize ?? 8, dpmm);
        object.fields.forEach((field, index) => {
          const value = resolveTokens(`{{${field}}}`, sampleData);
          const row = object.showLabels ? `${field}: ${value}` : value;
          lines.push(`TEXT ${x},${y + index * rowHeightDots},"${font}",0,${multiplier},${multiplier},"${escapeTspl(row)}"`);
        });
        break;
      }

      case 'logo':
      case 'image':
        skippedImages += 1;
        break;
    }
  }

  lines.push(`PRINT ${copies},1`);
  // TSPL commands are newline-terminated; the trailing newline matters — without it the printer
  // sits waiting for the last command to finish.
  return { commands: `${lines.join('\r\n')}\r\n`, skippedImages };
}
